import { useMutation } from '@apollo/client';
import React, { memo, useContext, useState } from 'react';
import setIn from '../components/connectedForm/utils/setIn';
import ProductForm from '../views/product/ProductForm';
import { InfoButton } from './connectedForm/Button';
import SaveOrderLineProductTempMutation from '../graphql/mutations/sales/SaveOrderLineProductTempMutation.graphql';
import ConnectedFormContext from './Context/ConnectedFormContext';
import getClientMutationId from '../utils/getClientMutationId';
import normalizeVariables from '../graphql/schema/normalizeVariables';
import { useRefFunction } from '../utils/hooks';

import FormModal from './modals/FormModal';
import getIn from './connectedForm/utils/getIn';
import { ISalesOrderLine } from '../graphql/schema/p4types';
import { ISpecialOrderProductState } from '../utils/SalesOrderHooks';
import { useDispatch } from 'react-redux';

type IProductFormModalProps = {
  closeModal: any;
  activeTabId?: string;
  onChange?: (product: ISalesOrderLine) => void;
  modalState: {
    modalOpen?: boolean;
    productUuid?: string;
    rowIdx?: number;
    id?: number;
    dataid?: string;
    fieldNameOverride?: any;
  };
  specialOrder?: boolean;
  luckins?: boolean;
  readOnly?: boolean;
  customChange?: Function;
  miniForm?: boolean;
};

const ProductFormModal = memo(
  ({
    closeModal,
    activeTabId,
    onChange,
    modalState: {
      modalOpen: isOpen,
      productUuid: newProductUuid,
      rowIdx,
      id,
      dataid,
      fieldNameOverride = 'product',
    } = {},
    specialOrder = false,
    luckins = false,
    readOnly = false,
    miniForm: miniFormFromProps = true,
  }: IProductFormModalProps) => {
    const [miniForm, setMiniForm] = useState(!!miniFormFromProps);

    const actionButtons = specialOrder
      ? [
          {
            description: miniForm ? 'Full Form' : 'Mini Form',
            fn: () => setMiniForm(!miniForm),
            individualButton: true,
            component: InfoButton,
            alignLeft: true,
            ignorePermissions: true,
          },
        ]
      : [];

    const isTemporaryDefault = specialOrder || luckins;

    const { values } = useContext(ConnectedFormContext);
    const value = getIn(values, dataid || `orderLines.${rowIdx}`);

    const onCloseProductModal = (product) => {
      if (readOnly) {
        //Read only - Do not update/remove the Product (E.g. 'Viewing from Item Information, rather than alt products).
        closeModal();
        return;
      }
      let lineValue = value || {}; //

      lineValue = setIn(lineValue, fieldNameOverride, product);
      lineValue = setIn(lineValue, 'description', product?.description);
      lineValue = setIn(lineValue, 'components', product?.components);
      onChange?.(lineValue);
      closeModal();
    };

    return (
      <FormModal
        modalOpen={!!isOpen}
        setModalOpen={closeModal}
        styles={{
          main: {
            minWidth: miniForm ? undefined : '80vw',
            paddingBottom: miniForm ? '20px' : undefined,
          },
        }}
      >
        <ProductForm
          data-testid={dataid || `orderLines.${rowIdx}`}
          miniForm={miniForm}
          actionButtons={actionButtons}
          temporaryDefault={isTemporaryDefault}
          activeTabId={activeTabId}
          overrideParams={isTemporaryDefault ? { uuid: newProductUuid } : { id: id }}
          readOnly={readOnly}
          modalClose={true}
          onClose={onCloseProductModal}
        />
      </FormModal>
    );
  }
);

type ISalesOrderProductSaveModalProps = {
  modalState: ISpecialOrderProductState;
  closeModal: () => void;
  specialOrder?: boolean;
  miniForm?: boolean;
  luckins?: boolean;
};

export const SalesOrderProductSaveModal = ({
  modalState,
  closeModal,
  specialOrder,
  miniForm,
  luckins,
}: ISalesOrderProductSaveModalProps) => {
  const dispatch = useDispatch();
  const [temporarySaveSalesOrderLine] = useMutation(SaveOrderLineProductTempMutation);
  const connectedForm = useContext(ConnectedFormContext);
  const minimalHeader = {
    id: connectedForm.values?.id,
    uuid: connectedForm.values?.uuid,
    concurrencyVersion: connectedForm.values?.concurrencyVersion,
    customer: connectedForm.values?.customer,
    currency: connectedForm.values?.currency,
    foreignCurrency: connectedForm.values?.foreignCurrency,
    exchangeRate: connectedForm.values?.exchangeRate,
    vatBusinessCategory: connectedForm.values?.vatBusinessCategory,
    vatInclusive: connectedForm.values?.vatInclusive,
    defaultPriceBand: connectedForm.values?.defaultPriceBand,
  };

  //Need to be sure we use an up-to-date call with correct header information, so using ref function.
  const onChange = useRefFunction((newValue: ISalesOrderLine) => {
    const value = { ...newValue, salesOrder: minimalHeader };
    const salesOrderLineDataid = `orderLines.${modalState.rowIdx}`;

    temporarySaveSalesOrderLine({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          salesOrderLine: normalizeVariables(value),
        },
      },
    }).then((result) => {
      //Update the store for this particular row with the updated details from the services.
      const singleRowResult = result.data.salesOrderLineTemporary.result;
      dispatch(connectedForm.change(salesOrderLineDataid, singleRowResult));
    });
  });

  return (
    <ProductFormModal
      modalState={modalState}
      closeModal={closeModal}
      specialOrder={specialOrder}
      onChange={onChange}
      miniForm={miniForm}
      luckins={luckins}
    />
  );
};

export default ProductFormModal;
